import axios from 'axios'

// HistoryMonitor 只允许调用产品级接口：
// 1) full summary：低频加载覆盖、计划、质检与范围；
// 2) runtime telemetry：轻量轮询 Dedicated Owner 当前阶段和进度；
// 3) guarded config：单目标 read -> preview -> explicit apply。
// 禁止在前端接入 S0-S4 / D* / Q* / W* / R* 施工工具。
const API_BASE = process.env.VUE_APP_API_URL || '/api'
const RUNTIME_PATH = '/hddb/history-monitor/runtime'
const RUNTIME_ROUTE = '/api/hddb/history-monitor/runtime'
const RUNTIME_CONTRACT_VERSION = 'hddb_history_monitor_runtime_rest_v2'
const CONFIG_PATH = '/hddb/history-monitor/config'
const CONFIG_PREVIEW_PATH = '/hddb/history-monitor/config/preview'
const CONFIG_APPLY_PATH = '/hddb/history-monitor/config/apply'
const CONFIG_ROUTE = '/api/hddb/history-monitor/config'
const CONFIG_PREVIEW_ROUTE = '/api/hddb/history-monitor/config/preview'
const CONFIG_APPLY_ROUTE = '/api/hddb/history-monitor/config/apply'
const CONFIG_REST_CONTRACT_VERSION = 'hddb_history_monitor_config_rest_v1'
const CONFIG_READ_CONTRACT_VERSION = 'hddb_history_monitor_config_read_v1'
const CONFIG_PREVIEW_CONTRACT_VERSION = 'hddb_history_monitor_config_preview_v1'
const CONFIG_APPLY_CONTRACT_VERSION = 'hddb_history_monitor_config_apply_v1'
const CONFIG_EDITABLE_FIELDS = ['status', 'configured_time']
const CONFIG_TIME_RE = /^(?:(?:19|20|21|22):[0-5][0-9]|23:00)$/

const historyMonitorClient = axios.create({
  baseURL: API_BASE,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json'
  }
})

historyMonitorClient.interceptors.request.use(
  config => {
    if (!config.monitorSilent) {
      console.log('[HDDB-HISTORY-MONITOR] ' + (config.method ? config.method.toUpperCase() : '') + ' ' + config.url)
    }
    return config
  },
  error => Promise.reject(error)
)

historyMonitorClient.interceptors.response.use(
  response => response.data,
  error => {
    const message = (error.response && error.response.data && error.response.data.detail) || error.message || '历史库监控请求失败'
    return Promise.reject(new Error(message))
  }
)

function sameArray(left, right) {
  return Array.isArray(left) &&
    Array.isArray(right) &&
    left.length === right.length &&
    left.every((value, index) => value === right[index])
}

function hasText(value) {
  return typeof value === 'string' && value.length > 0
}

function hasOwn(source, key) {
  return Boolean(source && Object.prototype.hasOwnProperty.call(source, key))
}

function validateRuntimeRouteContract(payload) {
  const route = payload && payload.http_route_summary
  const valid = Boolean(
    route &&
    route.route === RUNTIME_ROUTE &&
    route.contract_version === RUNTIME_CONTRACT_VERSION &&
    route.lightweight_runtime_only === true &&
    route.process_local_only === false &&
    route.cross_process_shared_snapshot_supported === true &&
    route.local_process_fallback_supported === true &&
    route.full_product_summary_called === false &&
    route.reads_live_database === false &&
    route.reads_shared_runtime_file === true &&
    route.calls_provider === false &&
    route.downloads_data === false &&
    route.creates_or_mutates_queue === false &&
    route.starts_runtime_scheduler_or_worker === false &&
    route.new_mcp_tool_created === false &&
    route.new_http_endpoint_created === false &&
    route.read_only === true &&
    route.cache_policy === 'no_store'
  )
  if (!valid) throw new Error('runtime REST 路由合同校验失败')
  return payload
}

function expectedConfigRoute(mode) {
  if (mode === 'preview') return CONFIG_PREVIEW_ROUTE
  if (mode === 'apply') return CONFIG_APPLY_ROUTE
  return CONFIG_ROUTE
}

function validateConfigRouteContract(payload, mode) {
  const route = payload && payload.http_route_summary
  const valid = Boolean(
    route &&
    route.route === expectedConfigRoute(mode) &&
    route.contract_version === CONFIG_REST_CONTRACT_VERSION &&
    route.mode === mode &&
    route.single_target_only === true &&
    sameArray(route.editable_fields, CONFIG_EDITABLE_FIELDS) &&
    route.reads_live_database === true &&
    route.may_write_single_configuration_row === (mode === 'apply') &&
    route.calls_generic_scheduler === false &&
    route.calls_target_only_runtime_start === false &&
    route.starts_or_stops_runtime === false &&
    route.starts_scheduler_or_worker === false &&
    route.creates_or_mutates_queue === false &&
    route.claims_or_refreshes_lease === false &&
    route.calls_provider === false &&
    route.downloads_data === false &&
    route.writes_history_bar_or_event_rows === false &&
    route.executes_ddl === false &&
    route.new_mcp_tool_created === false &&
    route.cache_policy === 'no_store'
  )
  if (!valid) throw new Error(`config ${mode} REST 路由合同校验失败`)
}

function validateConfigRuntimeGuard(runtime, requireIdle) {
  const validShape = Boolean(
    runtime &&
    ['running', 'stopped', 'degraded', 'unknown'].includes(runtime.runtime_status) &&
    typeof runtime.current_tick_in_flight === 'boolean' &&
    typeof runtime.current_action_in_flight === 'boolean' &&
    typeof runtime.configuration_change_allowed_now === 'boolean' &&
    runtime.raw_runtime_payload_exposed === false
  )
  if (!validShape) throw new Error('配置 runtime guard 合同校验失败')
  if (requireIdle && !(
    runtime.runtime_status === 'stopped' &&
    runtime.current_tick_in_flight === false &&
    runtime.current_action_in_flight === false &&
    runtime.configuration_change_allowed_now === true
  )) {
    throw new Error('配置修改要求 runtime 已停止且无执行中动作')
  }
}

function validatePublicConfiguration(configuration) {
  const valid = Boolean(
    configuration &&
    ['active', 'paused'].includes(configuration.status) &&
    typeof configuration.enabled === 'boolean' &&
    CONFIG_TIME_RE.test(configuration.configured_time || '') &&
    configuration.allowed_window_start === '19:00' &&
    configuration.allowed_window_end === '23:00' &&
    configuration.timezone === 'Asia/Shanghai' &&
    typeof configuration.gray_enabled === 'boolean' &&
    hasText(configuration.config_revision_digest) &&
    sameArray(configuration.editable_fields, CONFIG_EDITABLE_FIELDS) &&
    configuration.window_end_editable === false &&
    configuration.weekend_editable === false &&
    configuration.gray_editable === false &&
    configuration.weekend_policy === 'same_daily_window_including_weekends' &&
    configuration.takes_effect_on_next_target_only_runtime_start === true &&
    configuration.does_not_reload_running_scheduler === true &&
    configuration.raw_task_id_exposed === false
  )
  if (!valid) throw new Error('配置摘要合同校验失败')
  return configuration
}

function validateConfigSafety(payload, mode) {
  const safety = payload && payload.safety_summary
  const idempotentNoChangeWithoutColumnFlag = mode === 'apply' &&
    payload &&
    payload.status === 'applied_no_change' &&
    !hasOwn(payload, 'raw_columns_exposed')
  const rawColumnsSafe = payload && (
    payload.raw_columns_exposed === false ||
    idempotentNoChangeWithoutColumnFlag
  )
  const valid = Boolean(
    safety &&
    safety.calls_generic_scheduler === false &&
    safety.calls_target_only_runtime_start === false &&
    safety.starts_runtime === false &&
    safety.stops_runtime === false &&
    safety.starts_scheduler === false &&
    safety.starts_worker === false &&
    safety.creates_queue === false &&
    safety.claims_queue_task === false &&
    safety.refreshes_lease === false &&
    safety.calls_provider === false &&
    safety.downloads_data === false &&
    safety.writes_history_rows === false &&
    safety.writes_bar_rows === false &&
    safety.writes_event_rows === false &&
    safety.executes_ddl === false &&
    safety.changes_schema === false &&
    safety.raw_rows_exposed === false &&
    safety.raw_sql_exposed === false &&
    safety.database_url_exposed === false &&
    safety.secret_values_exposed === false &&
    safety.token_values_exposed === false &&
    safety.raw_task_payload_exposed === false &&
    payload.raw_rows_exposed === false &&
    rawColumnsSafe
  )
  if (!valid) throw new Error(`config ${mode} 安全合同校验失败`)
}

function validateConfigRead(payload) {
  validateConfigRouteContract(payload, 'read')
  if (!(payload && payload.ok === true && payload.status === 'ok')) {
    throw new Error((payload && payload.blocked_reason) || '配置读取被阻断')
  }
  if (payload.contract_version !== CONFIG_READ_CONTRACT_VERSION || payload.read_only !== true) {
    throw new Error('配置读取合同版本校验失败')
  }
  validatePublicConfiguration(payload.configuration)
  validateConfigRuntimeGuard(payload.runtime_guard, false)
  if (payload.configuration_change_allowed_now !== payload.runtime_guard.configuration_change_allowed_now) {
    throw new Error('配置读取 runtime guard 映射不一致')
  }
  validateConfigSafety(payload, 'read')
  return payload
}

function validateConfigPreview(payload) {
  validateConfigRouteContract(payload, 'preview')
  if (payload && payload.ok === false && payload.status === 'blocked') return payload
  const valid = Boolean(
    payload &&
    payload.ok === true &&
    payload.status === 'preview_ready' &&
    payload.contract_version === CONFIG_PREVIEW_CONTRACT_VERSION &&
    hasText(payload.preview_id) &&
    hasText(payload.preview_digest) &&
    hasText(payload.expected_before_digest) &&
    hasText(payload.preview_expires_at) &&
    Array.isArray(payload.changed_fields) &&
    payload.changed_fields.every(field => CONFIG_EDITABLE_FIELDS.includes(field)) &&
    payload.changed_field_count === payload.changed_fields.length &&
    payload.no_change === (payload.changed_fields.length === 0) &&
    payload.can_apply === true &&
    payload.requires_explicit_confirmation === true &&
    payload.configuration_written_now === false
  )
  if (!valid) throw new Error('配置 Preview 合同校验失败')
  validatePublicConfiguration(payload.before)
  validatePublicConfiguration(payload.after)
  validateConfigRuntimeGuard(payload.runtime_guard, true)
  validateConfigSafety(payload, 'preview')
  return payload
}

function validateConfigApply(payload) {
  validateConfigRouteContract(payload, 'apply')
  if (payload && payload.ok === false && payload.status === 'blocked') return payload
  const applied = payload && payload.status === 'applied'
  const noChange = payload && payload.status === 'applied_no_change'
  const route = payload && payload.http_route_summary
  const valid = Boolean(
    payload &&
    payload.ok === true &&
    (applied || noChange) &&
    payload.contract_version === CONFIG_APPLY_CONTRACT_VERSION &&
    Number.isInteger(payload.updated_rows) &&
    payload.updated_rows === (applied ? 1 : 0) &&
    Array.isArray(payload.updated_fields) &&
    payload.updated_fields.every(field => CONFIG_EDITABLE_FIELDS.includes(field)) &&
    payload.configuration_written_now === applied &&
    route.configuration_written_now === applied &&
    payload.scheduler_reloaded_now !== true &&
    payload.runtime_started_now !== true &&
    payload.runtime_stopped_now !== true
  )
  if (!valid) throw new Error('配置 Apply 合同校验失败')
  validatePublicConfiguration(payload.readback)
  validateConfigRuntimeGuard(payload.runtime_guard, true)
  validateConfigSafety(payload, 'apply')
  return payload
}

export const historyMonitorApi = {
  // full summary：页面首次进入及低频静默刷新使用。
  getSummary: (options = {}) => historyMonitorClient.get('/hddb/history-monitor/summary', options),

  // shared-owner runtime telemetry：固定只读 GET，3 秒轮询由页面插件负责。
  getRuntime: async () => {
    const payload = await historyMonitorClient.get(RUNTIME_PATH, {
      timeout: 5000,
      monitorSilent: true
    })
    return validateRuntimeRouteContract(payload)
  },

  // guarded config read：单目标、provider-free、no-store。
  getConfig: async () => {
    const payload = await historyMonitorClient.get(CONFIG_PATH, {
      timeout: 10000,
      monitorSilent: true
    })
    return validateConfigRead(payload)
  },

  // guarded config preview：只生成短期 Preview，不写配置。
  previewConfig: async requested => {
    const payload = await historyMonitorClient.post(CONFIG_PREVIEW_PATH, requested, {
      timeout: 10000
    })
    return validateConfigPreview(payload)
  },

  // guarded config apply：只消费用户已确认的 Preview，最多更新一行配置。
  applyConfig: async confirmation => {
    const payload = await historyMonitorClient.post(CONFIG_APPLY_PATH, confirmation, {
      timeout: 15000
    })
    return validateConfigApply(payload)
  }
}

export default historyMonitorApi